import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';

// Marker used in blockOrder to remember where row 1 ends and row 2 begins.
export const FOOTER_LAYOUT_ROW_BREAK = 'ROW_BREAK';

interface Block {
  id: string;
  name: string;
  icon: string;
}

@Component({
  selector: 'aw-drag-drop-grid',
  standalone: true,
  imports: [DragDropModule],
  templateUrl: './drag-drop-grid.html',
  styleUrl: './drag-drop-grid.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DragDropGrid implements OnChanges {
  @Input() blocks: string[] = ['logoArea', 'addressArea', 'paymentArea'];
  @Input() customBlocks: Block[] = [];
  @Input() hiddenBlocks: string[] = [];

  @Output() blockOrderChanged = new EventEmitter<string[]>();
  @Output() hiddenBlocksChanged = new EventEmitter<string[]>();

  rows: Block[][] = [];
  hiddenBlockItems: Block[] = [];

  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );

  private readonly blockMeta: Record<string, Block> = {
    logoArea: {
      id: 'logoArea',
      name: `${this.cmsTextProvider.footer.block} 1`,
      icon: 'image',
    },
    addressArea: {
      id: 'addressArea',
      name: `${this.cmsTextProvider.footer.block} 2`,
      icon: 'text_fields',
    },
    paymentArea: {
      id: 'paymentArea',
      name: `${this.cmsTextProvider.footer.block} 3`,
      icon: 'payments',
    },
  };

  ngOnChanges(): void {
    this.rebuildRows();
  }

  private rebuildRows(): void {
    const blockMeta = {
      ...this.blockMeta,
      ...Object.fromEntries(this.customBlocks.map(block => [block.id, block])),
    };
    const items = (this.blocks ?? []).filter(
      id => id !== 'linkArea' && !this.hiddenBlocks?.includes(id)
    );

    const breakIndex = items.indexOf(FOOTER_LAYOUT_ROW_BREAK);
    let row1Items: string[];
    let row2Items: string[];

    if (breakIndex !== -1) {
      row1Items = items.slice(0, breakIndex);
      row2Items = items.slice(breakIndex + 1);
    } else {
      row1Items = items.slice(0, 2);
      row2Items = items.slice(2);
    }

    this.rows = [
      row1Items.map(id => blockMeta[id] ?? { id, name: id, icon: 'image' }),
      row2Items.map(id => blockMeta[id] ?? { id, name: id, icon: 'image' }),
    ];

    this.hiddenBlockItems = (this.hiddenBlocks ?? []).map(
      id => blockMeta[id] ?? { id, name: id, icon: 'image' }
    );
  }

  drop(event: CdkDragDrop<Block[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }
    const currentBlockOrder = [
      ...this.rows[0].map(b => b.id),
      FOOTER_LAYOUT_ROW_BREAK,
      ...this.rows[1].map(b => b.id),
    ];
    const currentHiddenBlocks = this.hiddenBlockItems.map(b => b.id);

    this.blockOrderChanged.emit(currentBlockOrder);
    this.hiddenBlocksChanged.emit(currentHiddenBlocks);
  }
}
